import { createApi } from "@reduxjs/toolkit/dist/query/react";
import {
  BranchDTO,
  NearerBranchRequestBody,
  NearerBranchResponseData,
} from "../../types/branch/BranchDTO";
import { Response } from "../../types/response/Response";
import { ZoneDTO } from "../../types/branch/ZoneDTO";
import {
  DeliveryZonesByIdResponse,
  DeliveryZonesResponse,
} from "../../types/branch/DeliveryZoneDTO";
import { branchApiQuery, branchApiUserAuthQuery } from "../utils/Extensions";

export interface ZoneFileResponse {
  data: ZoneDTO[] | null;
  message: string;
  success: boolean;
}

export const branchApi = createApi({
  reducerPath: "branch",
  baseQuery: branchApiQuery,
  tagTypes: [
    "nearer-branches",
    "branches",
    "zones",
    "delivery-zones",
    "delivery-zones-by-id",
  ],
  endpoints: (builder) => ({
    getBranches: builder.query<Response<BranchDTO[]>, Partial<void>>({
      query: () => "/v0/organizations",
      providesTags: ["branches"],
    }),
    getNearestBranches: builder.query<
      Response<NearerBranchResponseData>,
      Partial<NearerBranchRequestBody>
    >({
      query: (body) => ({
        method: "POST",
        url: "/v1/organizations/nearer-branches",
        body,
      }),
      providesTags: ["nearer-branches"],
    }),
    getZones: builder.query<ZoneFileResponse, string | undefined>({
      query: (id) => ({
        method: "GET",
        url: `/v1/delivery-zones/${id}/file`,
      }),
      providesTags: ["zones"],
    }),
    getDeliveryZonesById: builder.query<
      DeliveryZonesByIdResponse,
      string | undefined
    >({
      async queryFn(id, api) {
        if (!id) {
          return {
            error: {
              status: 400,
              statusText: "Bad Request",
              data: "Missing zone id",
            },
          };
        }
        const result = await branchApiUserAuthQuery(
          {
            method: "GET",
            url: `/v1/delivery-zones/${id}`,
          },
          api,
          {},
        );
        if (result.error) {
          return { error: result.error };
        }
        return {
          data: result.data as DeliveryZonesByIdResponse,
          meta: result.meta,
        };
      },
      providesTags: ["zones"],
    }),
    getDeliveryZones: builder.query<DeliveryZonesResponse, void>({
      async queryFn(_arg, api) {
        const result = await branchApiUserAuthQuery(
          {
            method: "GET",
            url: "/v1/delivery-zones",
          },
          api,
          {},
        );
        if (result.error) {
          return { error: result.error };
        }
        return {
          data: result.data as DeliveryZonesResponse,
          meta: result.meta,
        };
      },
      providesTags: ["delivery-zones"],
    }),
    deleteDeliveryZone: builder.mutation<void, number>({
      async queryFn(id, api) {
        const result = await branchApiUserAuthQuery(
          {
            method: "DELETE",
            url: `/v1/delivery-zones/${id}`,
          },
          api,
          {},
        );
        if (result.error) {
          return { error: result.error };
        }
        return { data: undefined as void };
      },
      invalidatesTags: ["delivery-zones"],
    }),
  }),
});

export const {
  useLazyGetNearestBranchesQuery,
  useGetBranchesQuery,
  useGetZonesQuery,
  useGetDeliveryZonesQuery,
  useDeleteDeliveryZoneMutation,
  useGetDeliveryZonesByIdQuery,
} = branchApi;
